// Volet latéral « Nouveau patient » (Prompt 12 §2) — création express dans le
// même volet flottant que l'aperçu patient. Aucune popup. Détection locale des
// champs, doublons, champs facultatifs, consentement, 3 boutons de création +
// toast « Annuler ». Réutilisé depuis la Patientèle (bouton + Patient, touche N)
// et depuis la recherche ⌘K (préremplissage).
import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Mail, MapPin, Cake,
  Tag as TagIcon, Pin, ShieldCheck, UserPlus, CalendarPlus, Repeat, Search,
} from "lucide-react";
import { VoiceRecorderButton } from "@/components/VoiceRecorderButton";
import PatientDetectForm from "@/components/PatientDetectForm";
import { tagChipStyle } from "@/lib/tagColor";
import {
  LABELS, parseInput, parsePhone, findDuplicates,
  DUP_DEBOUNCE, UNDO_MS,
} from "@/lib/patientParse";

export default function NewPatientPanel({ prefill = "", onClose, onCreated, onReopen, nav, openCreerRdv, secteurs = [], tags = [] }) {
  const [text, setText] = useState(prefill);
  const [overrides, setOverrides] = useState({}); // { prenom, nom, email, phone(raw) }
  const [editing, setEditing] = useState(null); // clé de chip en édition
  const [editVal, setEditVal] = useState("");
  const [patients, setPatients] = useState([]);
  const [dups, setDups] = useState([]);
  const [expanded, setExpanded] = useState({}); // champs facultatifs dépliés
  const [opt, setOpt] = useState({ email: "", adresse: "", adresse_voie: "", code_postal: "", ville: "", lat: null, lng: null, date_naissance: "", secteurs: [], tags: [], note: "" });
  const [consent, setConsent] = useState(true);
  const [swapped, setSwapped] = useState(false); // inversion prénom/nom conservée en continuant la frappe
  const [saving, setSaving] = useState(false);
  const [askClose, setAskClose] = useState(false);
  const inputRef = useRef(null);
  const asideRef = useRef(null);

  // Focus auto + chargement de la patientèle (détection de doublons).
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 30); }, []);
  useEffect(() => { api.get("/patients").then((r) => setPatients(r.data)).catch(() => {}); }, []);

  const base = useMemo(() => parseInput(text), [text]);
  const fields = useMemo(() => {
    const bp = swapped ? base.nom : base.prenom;
    const bn = swapped ? base.prenom : base.nom;
    return {
      prenom: overrides.prenom !== undefined ? overrides.prenom : bp,
      nom: overrides.nom !== undefined ? overrides.nom : bn,
      email: (overrides.email !== undefined ? overrides.email : base.email) || opt.email || "",
      phone: overrides.phone !== undefined ? parsePhone(overrides.phone) : base.phone,
    };
  }, [base, overrides, opt.email, swapped]);

  // Doublons (debounce)
  useEffect(() => {
    const t = setTimeout(() => setDups(findDuplicates(patients, fields)), DUP_DEBOUNCE);
    return () => clearTimeout(t);
  }, [patients, fields]);

  const hasContact = (fields.phone && fields.phone.value && !fields.phone.incomplete) || !!fields.email;
  const canCreate = !!fields.prenom; // moyen de contact (tél/email) facultatif
  const missing = !fields.prenom ? "Ajoutez un prénom" : "";
  const dirty = !!text.trim() || Object.keys(overrides).length > 0 || Object.values(expanded).some(Boolean);

  const reset = () => {
    setText(""); setOverrides({}); setExpanded({}); setConsent(true); setSwapped(false);
    setOpt({ email: "", adresse: "", adresse_voie: "", code_postal: "", ville: "", lat: null, lng: null, date_naissance: "", secteurs: [], tags: [], note: "" });
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const handleClose = () => { if (dirty) setAskClose(true); else onClose(); };

  // Échap : 1er appui ouvre la confirmation (si saisie), 2e appui = Abandonner.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); if (editing) setEditing(null); else if (askClose) onClose(); else handleClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line
  }, [editing, dirty, askClose]);

  // Fermeture auto si rien n'a été saisi/modifié et clic en dehors du volet.
  useEffect(() => {
    const onDown = (e) => {
      if (dirty || askClose) return;
      if (asideRef.current && !asideRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
    // eslint-disable-next-line
  }, [dirty, askClose]);

  const buildPayload = () => {
    const p = {
      prenom: fields.prenom, nom: fields.nom || "",
      telephone: fields.phone ? fields.phone.value : "",
      email: fields.email || "",
    };
    if (opt.adresse) p.adresse = opt.adresse;
    if (opt.adresse_voie) p.adresse_voie = opt.adresse_voie;
    if (opt.code_postal) p.code_postal = opt.code_postal;
    if (opt.ville) p.ville = opt.ville;
    if (opt.lat != null) p.lat = opt.lat;
    if (opt.lng != null) p.lng = opt.lng;
    if (opt.date_naissance) p.date_naissance = opt.date_naissance;
    if (opt.secteurs.length) p.secteurs = opt.secteurs;
    if (opt.tags.length) p.tags = opt.tags;
    if (opt.note.trim()) p.note = opt.note.trim();
    if (consent) {
      const date = new Date().toISOString();
      p.consentements = [
        { canal: "sms", accorde: true, date, source: LABELS.consentSource },
        { canal: "email", accorde: true, date, source: LABELS.consentSource },
      ];
    }
    return p;
  };

  const undo = async (id, savedText) => {
    try { await api.delete(`/patients/${id}`); } catch (_) { /* ignore */ }
    if (onReopen) onReopen(savedText); else if (nav) nav(`/patients?newpatient=1&npq=${encodeURIComponent(savedText)}`);
  };

  const create = async (action) => {
    if (!canCreate || saving) return;
    setSaving(true);
    const savedText = text;
    try {
      const { data: p } = await api.post("/patients", buildPayload());
      toast(`${p.prenom} ${p.nom || ""}`.trim() + " créé", {
        action: { label: "Annuler", onClick: () => undo(p.id, savedText) },
        duration: UNDO_MS,
      });
      onCreated?.(p);
      if (action === "fiche") { onClose(); nav(`/patients/${p.id}?tab=infos`); }
      else if (action === "rdv") { onClose(); openCreerRdv?.({ patientId: p.id }); }
      else { setPatients((prev) => [p, ...prev]); reset(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Création impossible"); }
    finally { setSaving(false); }
  };

  const onInputKey = (e) => { if (!canCreate) return; if (e.metaKey || e.ctrlKey) create("rdv"); else if (e.shiftKey) create("another"); else create("fiche"); };

  // Transcription vocale générique pour la note (clé OpenAI des Paramètres).
  const transcribeNote = async (blob) => {
    const fd = new FormData(); fd.append("file", blob, "note.webm");
    const { data } = await api.post("/settings/transcribe-note", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.text;
  };

  const OPTS = [
    ["email", "Email", Mail],
    ["adresse", "Adresse", MapPin],
    ["date_naissance", "Date de naissance", Cake],
    ["secteurs", "Secteur", MapPin],
    ["tags", "Tag", TagIcon],
    ["note", "Note", Pin],
  ];
  const optFilled = (k) => k === "secteurs" || k === "tags" ? opt[k].length > 0 : k === "email" ? !!fields.email : !!opt[k];

  return (
    <aside data-testid="new-patient-panel" ref={asideRef} className="w-[420px] max-w-full shrink-0 sx-card sticky self-start flex flex-col sx-fade-in"
      style={{ borderRadius: 14, boxShadow: "var(--sh-float)", top: "calc(var(--header-h, 96px) + 16px)", maxHeight: "calc(100vh - var(--header-h, 96px) - 32px)", overflow: "hidden" }}>
      {/* En-tête */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
        <h2 className="font-bold tracking-[-0.01em]" style={{ fontSize: 20, color: "var(--ink)" }}>{LABELS.title}</h2>
        {askClose ? (
          <div className="flex items-center gap-3 text-[12.5px]" data-testid="np-ask-close">
            <button data-testid="np-abandon" onClick={onClose} className="inline-flex items-center gap-1 font-semibold" style={{ color: "oklch(0.55 0.16 25)" }}><X className="w-3.5 h-3.5" />Abandonner</button>
            <button onClick={() => setAskClose(false)} className="font-medium" style={{ color: "var(--muted)" }}>Continuer</button>
          </div>
        ) : (
          <button data-testid="np-close" onClick={handleClose} className="inline-flex items-center gap-1 text-[12.5px]" style={{ color: "var(--muted)" }}><X className="w-3.5 h-3.5" />Esc</button>
        )}
      </div>

      {/* Corps défilant */}
      <div className="flex-1 overflow-y-auto px-5 pb-4">
        <PatientDetectForm
          text={text} setText={setText}
          overrides={overrides} setOverrides={setOverrides}
          swapped={swapped} setSwapped={setSwapped}
          editing={editing} setEditing={setEditing}
          editVal={editVal} setEditVal={setEditVal}
          fields={fields} dups={dups} inputRef={inputRef}
          idPrefix="np" showEmailChip={false}
          onEnter={onInputKey}
          dupActionLabel={LABELS.open}
          onDupAction={(p) => { onClose(); nav(`/patients/${p.id}`); }}
        />

        {/* Champs facultatifs */}
        <div className="mt-4">
          <div className="text-[13px] font-semibold mb-2" style={{ color: "var(--ink)" }}>{LABELS.addMore}</div>
          <div className="flex flex-wrap gap-1.5">
            {OPTS.map(([k, l, Icon]) => {
              const on = !!expanded[k] || optFilled(k);
              return (
                <button key={k} type="button" data-testid={`np-opt-${k}`} onClick={() => setExpanded((e) => ({ ...e, [k]: !e[k] }))}
                  className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-full transition-colors"
                  style={on ? { background: "#141821", color: "#fff" } : { border: "1px dashed var(--border-input)", color: "var(--muted)" }}>
                  <Icon className="w-3 h-3" />{l}{optFilled(k) && (k === "email" || k === "date_naissance") ? `: ${String(k === "email" ? fields.email : opt[k]).slice(0, 40)}` : ""}
                </button>
              );
            })}
          </div>

          {/* Résumé persistant des valeurs choisies (visible même section repliée) */}
          <div className="mt-2 space-y-1.5" data-testid="np-opt-summary">
            {opt.adresse && !expanded.adresse && (
              <div className="flex items-start gap-1.5 text-[12px]" style={{ color: "var(--text-2)" }}><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--muted-2)" }} /><span>{opt.adresse}</span></div>
            )}
            {opt.secteurs.length > 0 && !expanded.secteurs && (
              <div className="flex flex-wrap gap-1.5">{opt.secteurs.map((id) => { const s = secteurs.find((x) => x.id === id); return s ? <span key={id} className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-full" style={{ background: "#f4f2ec", color: "var(--text-2)" }}><MapPin className="w-3 h-3" />{s.nom}</span> : null; })}</div>
            )}
            {opt.tags.length > 0 && !expanded.tags && (
              <div className="flex flex-wrap gap-1.5" data-testid="np-tags-summary">{opt.tags.map((id) => { const t = tags.find((x) => x.id === id); return t ? <span key={id} className="inline-flex items-center rounded-full text-[11px] font-medium px-2 py-0.5" style={tagChipStyle(t.couleur)}>{t.titre}</span> : null; })}</div>
            )}
          </div>

          <div className="space-y-2.5 mt-2.5">
            {expanded.email && <input data-testid="np-opt-email-input" autoFocus value={fields.email} onChange={(ev) => setOverrides((o) => ({ ...o, email: ev.target.value }))} onBlur={() => setExpanded((e) => ({ ...e, email: false }))} placeholder="Email" className="w-full sx-input" />}
            {expanded.adresse && <AddressMini opt={opt} setOpt={setOpt} onDone={() => setExpanded((e) => ({ ...e, adresse: false }))} />}
            {expanded.date_naissance && <input data-testid="np-opt-dob-input" autoFocus type="date" value={opt.date_naissance} onChange={(e) => setOpt({ ...opt, date_naissance: e.target.value })} onBlur={() => setExpanded((e) => ({ ...e, date_naissance: false }))} className="w-full sx-input" />}
            {expanded.secteurs && <MultiSelect testid="np-opt-secteurs" items={secteurs} getLabel={(s) => s.nom} value={opt.secteurs} onChange={(ids) => setOpt({ ...opt, secteurs: ids })} onDone={() => setExpanded((e) => ({ ...e, secteurs: false }))} placeholder="Ajouter un secteur…" />}
            {expanded.tags && <MultiSelect testid="np-opt-tags" items={tags} getLabel={(t) => t.titre} value={opt.tags} onChange={(ids) => setOpt({ ...opt, tags: ids })} onDone={() => setExpanded((e) => ({ ...e, tags: false }))} placeholder="Ajouter un tag…" />}
            {expanded.note && (
              <div>
                <div className="flex items-center justify-end mb-1.5">
                  <VoiceRecorderButton testId="np-note-voice" label="Dicter la note" transcribe={transcribeNote} onText={(t) => setOpt((o) => ({ ...o, note: (o.note ? o.note.replace(/\s*$/, "") + " " : "") + t }))} />
                </div>
                <textarea data-testid="np-opt-note-input" autoFocus value={opt.note} onChange={(e) => setOpt({ ...opt, note: e.target.value })} rows={2} placeholder="Note interne…" className="w-full sx-input" style={{ height: "auto", paddingTop: 8 }} />
              </div>
            )}
          </div>
        </div>

        {/* Consentement */}
        <div className="mt-4 flex items-center justify-between gap-2 rounded-[10px] px-3 py-2.5" style={{ background: "#f7f7f5" }}>
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="w-4 h-4 shrink-0" style={{ color: "oklch(0.5 0.13 155)" }} />
            <span className="text-[12.5px]" style={{ color: "var(--ink)" }}>{LABELS.consent}</span>
          </div>
          <button data-testid="np-consent" onClick={() => setConsent((c) => !c)} role="switch" aria-checked={consent} className="shrink-0" style={{ width: 40, height: 22, borderRadius: 99, background: consent ? "var(--ink)" : "var(--border-input)", position: "relative", transition: "background .15s" }}>
            <span style={{ position: "absolute", top: 2, left: consent ? 20 : 2, width: 18, height: 18, borderRadius: 99, background: "#fff", transition: "left .15s" }} />
          </button>
        </div>
      </div>

      {/* Pied fixe */}
      <div className="shrink-0 px-5 py-4 space-y-2" style={{ borderTop: "1px solid var(--border)" }}>
        {!canCreate && <div className="text-[12px]" data-testid="np-missing" style={{ color: "var(--muted)" }}>{missing}</div>}
        <button data-testid="np-create-fiche" onClick={() => create("fiche")} disabled={!canCreate || saving} className="w-full flex items-center justify-center gap-2 rounded-[10px] font-semibold text-[14px]" style={{ height: 48, background: "#141821", color: "#fff", opacity: canCreate && !saving ? 1 : 0.5 }}>
          <UserPlus className="w-4 h-4" style={{ color: "#facc15" }} />{LABELS.createOpen}<span className="sx-kbd" style={{ marginLeft: 4 }}>↵</span>
        </button>
        <div className="flex items-center gap-2">
          <button data-testid="np-create-rdv" onClick={() => create("rdv")} disabled={!canCreate || saving} className="flex-1 flex items-center justify-center gap-1.5 rounded-[10px] font-semibold text-[13px]" style={{ height: 42, background: "#facc15", color: "#141821", opacity: canCreate && !saving ? 1 : 0.5 }}>
            <CalendarPlus className="w-4 h-4" />{LABELS.createRdv}
          </button>
          <button data-testid="np-create-another" onClick={() => create("another")} disabled={!canCreate || saving} className="flex-1 flex items-center justify-center gap-1.5 rounded-[10px] font-semibold text-[13px]" style={{ height: 42, border: "1px solid #e3e3de", color: "var(--ink)", opacity: canCreate && !saving ? 1 : 0.5 }}>
            <Repeat className="w-4 h-4" />{LABELS.createAnother}
          </button>
        </div>
      </div>
    </aside>
  );
}

// Saisie d'adresse assistée (Base Adresse Nationale, sans clé).
function AddressMini({ opt, setOpt, onDone }) {
  const [q, setQ] = useState(opt.adresse || "");
  const [sugs, setSugs] = useState([]);
  const tRef = useRef();
  useEffect(() => {
    if (q.trim().length < 3) { setSugs([]); return; }
    clearTimeout(tRef.current);
    tRef.current = setTimeout(async () => {
      try { const r = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=5`); const d = await r.json(); setSugs(d.features || []); } catch { setSugs([]); }
    }, 300);
  }, [q]);
  const choose = (f) => {
    const pr = f.properties, co = f.geometry?.coordinates || [];
    setOpt((o) => ({ ...o, adresse: pr.label || "", adresse_voie: pr.name || "", code_postal: pr.postcode || "", ville: pr.city || "", lng: co[0] ?? null, lat: co[1] ?? null }));
    setSugs([]);
    onDone?.();
  };
  return (
    <div className="relative">
      <div className="flex items-center gap-2 sx-input" style={{ paddingLeft: 10 }}>
        <Search className="w-4 h-4 shrink-0" style={{ color: "var(--muted-2)" }} />
        <input autoFocus data-testid="np-opt-adresse-input" value={q} onChange={(e) => { setQ(e.target.value); setOpt((o) => ({ ...o, adresse: e.target.value })); }} placeholder="Rechercher une adresse…" className="flex-1 bg-transparent outline-none text-[13.5px]" />
      </div>
      {sugs.length > 0 && (
        <div className="absolute z-40 left-0 right-0 mt-1 rounded-lg overflow-hidden" style={{ background: "#fff", border: "1px solid var(--border)", boxShadow: "var(--sh-float)" }}>
          {sugs.map((f, i) => <button key={f.properties.id || i} onMouseDown={(e) => { e.preventDefault(); choose(f); }} className="w-full text-left px-3 py-2 text-[13px] truncate hover:bg-slate-50" style={{ color: "var(--ink)" }}>{f.properties.label}</button>)}
        </div>
      )}
    </div>
  );
}

// Multi-sélection simple (secteurs / tags). Se referme à la perte de focus (onDone).
function MultiSelect({ testid, items, getLabel, value, onChange, onDone, placeholder }) {
  const sel = value.map((id) => items.find((x) => x.id === id)).filter(Boolean);
  const rest = items.filter((x) => !value.includes(x.id));
  return (
    <div data-testid={testid}>
      {sel.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {sel.map((it) => (
            <span key={it.id} className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-full" style={{ background: "#f4f2ec", color: "var(--text-2)" }}>
              {getLabel(it)}<button onClick={() => onChange(value.filter((x) => x !== it.id))} className="ml-0.5 hover:opacity-70"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <select autoFocus value="" onChange={(e) => { if (e.target.value) onChange([...value, e.target.value]); }} onBlur={() => onDone?.()} className="w-full sx-input text-[13px]">
        <option value="">{placeholder}</option>
        {rest.map((it) => <option key={it.id} value={it.id}>{getLabel(it)}</option>)}
      </select>
    </div>
  );
}
